/**
 * usePreviewPlayer — Synchronized video + audio playback
 *
 * Plays <video muted> while synchronizing generated segment audio
 * (TTS preview or Gemini premium) based on video.currentTime.
 *
 * When video enters a segment's time range → plays its audio
 * When video leaves → stops that audio and starts the next
 *
 * Phase 3: Integrates with RecorderHealthAPI for resource tracking.
 * - RAF sync loop registered via health.trackRAF('preview', 'syncLoop')
 * - Audio instances registered via health.trackAudio('preview', ...)
 *
 * Error Log: Maintains an internal errorLog array with structured error entries
 * (timestamp, source, message, details) for debugging display in the UI.
 */
import { useCallback, useRef, useState, useEffect } from 'react';
import type { useSegmentAudio } from './useSegmentAudio';
import type { RecorderHealthAPI } from './useRecorderHealth';


interface Segment {
  index: number;
  startSec: number;
  endSec: number;
  text: string;
}

type AudioSource = 'bruto' | 'preview' | 'premium';

export interface PreviewErrorEntry {
  id: string;
  timestamp: string;
  source: 'video' | 'audio' | 'tts' | 'sync' | 'play';
  message: string;
  details?: string;
}

const MAX_ERROR_LOG = 50;

export function usePreviewPlayer(
  segAudio: ReturnType<typeof useSegmentAudio> | undefined,
  ttsGetVoice?: () => SpeechSynthesisVoice | null,
  healthApi?: RecorderHealthAPI
) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activeSegIdx, setActiveSegIdx] = useState<number | null>(null);
  const [isSeeking, setIsSeeking] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [errorLog, setErrorLog] = useState<PreviewErrorEntry[]>([]);
  const [isFrozen, setIsFrozen] = useState(false);
  const isFrozenRef = useRef(false);

  // Refs for sync loop
  const segmentsRef = useRef<Segment[]>([]);
  const audioSourceRef = useRef<AudioSource>('bruto');
  const rafRef = useRef<number | null>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const currentUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const lastActiveSegRef = useRef<number | null>(null);
  const lastTimeUpdateMsRef = useRef(0);
  const activeSegIdxRef = useRef<number | null>(null);

  // New refs for virtual timeline extension
  const isPlayingRef = useRef(false);
  const playheadTimeRef = useRef(0);
  const lastSystemTimeRef = useRef(0);
  const isVirtualExtensionRef = useRef(false);

  // ── Original audio (bruto mode) — separate audio track ──
  const originalAudioRef = useRef<HTMLAudioElement | null>(null);
  const originalAudioUrlRef = useRef<string>('');

  // ── Voice tracking refs (multi-voice versioning) ──
  const currentPremiumVoiceRef = useRef<string>('');
  const currentPreviewVoiceRef = useRef<string>('');

  const setVoiceRefs = useCallback((premiumVoice: string, previewVoice: string) => {
    currentPremiumVoiceRef.current = premiumVoice;
    currentPreviewVoiceRef.current = previewVoice;
  }, []);

  const setOriginalAudioUrl = useCallback((url: string) => {
    originalAudioUrlRef.current = url;
    // Pre-create audio element for instant playback
    if (url) {
      const audio = new Audio(url);
      audio.preload = 'auto';
      originalAudioRef.current = audio;
      console.log('[PreviewPlayer] 🔊 Áudio original configurado:', url.substring(0, 60));
    } else {
      originalAudioRef.current = null;
    }
  }, []);

  // ── Error logging helper ──
  const pushError = useCallback((source: PreviewErrorEntry['source'], message: string, details?: string) => {
    const entry: PreviewErrorEntry = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      timestamp: new Date().toLocaleTimeString('pt-BR'),
      source,
      message,
      details,
    };
    console.error(`[PreviewPlayer][${source}] ${message}`, details || '');
    setErrorLog(prev => {
      const next = [entry, ...prev];
      return next.length > MAX_ERROR_LOG ? next.slice(0, MAX_ERROR_LOG) : next;
    });
  }, []);

  const clearErrorLog = useCallback(() => {
    setErrorLog([]);
  }, []);

  // Update segments ref (avoid re-renders in sync loop)
  const setSegments = useCallback((segs: Segment[]) => {
    segmentsRef.current = segs;
    const vid = videoRef.current;
    const videoDuration = vid ? vid.duration : 0;
    const lastSegEnd = segs.length > 0 ? segs[segs.length - 1].endSec : 0;
    const d = isFinite(videoDuration) && !isNaN(videoDuration) ? videoDuration : 0;
    setDuration(Math.max(d, lastSegEnd));
  }, []);

  const setAudioSourceMode = useCallback((mode: AudioSource) => {
    audioSourceRef.current = mode;
  }, []);

  const syncLoopRef = useRef<() => void>(() => {});

  const checkAndUnfreeze = useCallback(() => {
    if (isFrozenRef.current) {
      const vid = videoRef.current;
      if (vid) {
        if (isVirtualExtensionRef.current) {
          isFrozenRef.current = false;
          setIsFrozen(false);
          lastSystemTimeRef.current = performance.now();
          if (rafRef.current === null) {
            rafRef.current = requestAnimationFrame(syncLoopRef.current);
            if (healthApi && rafRef.current) {
              healthApi.trackRAF('preview', 'previewSyncLoop', rafRef.current);
            }
          }
        } else {
          vid.play().then(() => {
            isFrozenRef.current = false;
            setIsFrozen(false);
            // Restart sync loop if it exited during freeze transition
            if (rafRef.current === null) {
              rafRef.current = requestAnimationFrame(syncLoopRef.current);
              if (healthApi && rafRef.current) {
                healthApi.trackRAF('preview', 'previewSyncLoop', rafRef.current);
              }
            }
          }).catch((err) => {
            console.warn('[PreviewPlayer] Failed to resume video after freeze:', err);
            isFrozenRef.current = false;
            setIsFrozen(false);
          });
        }
      } else {
        isFrozenRef.current = false;
        setIsFrozen(false);
      }
    }
  }, [healthApi]);

  // ── Stop any current audio playback ──
  const stopCurrentAudio = useCallback(() => {
    isFrozenRef.current = false;
    setIsFrozen(false);
    if (currentAudioRef.current) {
      try {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
      } catch (e: any) {
        // Audio element may already be disposed
      }
      currentAudioRef.current = null;
      if (healthApi) healthApi.untrackAudio('previewSegAudio');
    }
    if (currentUtteranceRef.current) {
      try {
        window.speechSynthesis?.cancel();
      } catch (e: any) {
        // speechSynthesis not available
      }
      currentUtteranceRef.current = null;
      if (healthApi) healthApi.untrackAudio('previewTtsUtterance');
    }
    // NOTE: Do NOT reset lastActiveSegRef here — the sync loop uses it
    // to detect segment transitions. It's reset only by pause() or gap exit.
  }, [healthApi]);

  // ── Play segment audio (called from sync loop) ──
  const playSegmentAudioForSync = useCallback((segIndex: number) => {
    const mode = audioSourceRef.current;

    if (mode === 'preview') {
      // TTS preview via SpeechSynthesis
      if (!window.speechSynthesis) {
        pushError('tts', 'SpeechSynthesis não suportado neste navegador.', 'window.speechSynthesis is undefined');
        return;
      }
      if (!ttsGetVoice) {
        pushError('tts', 'Nenhuma função de voz TTS configurada.', 'ttsGetVoice is undefined');
        return;
      }

      stopCurrentAudio();
      lastActiveSegRef.current = segIndex;
      const seg = segmentsRef.current.find(s => s.index === segIndex);
      if (!seg) {
        pushError('tts', `Segmento #${segIndex} não encontrado nos metadados.`, `segmentsRef.length=${segmentsRef.current.length}`);
        return;
      }

      try {
        const voice = ttsGetVoice();
        const utt = new SpeechSynthesisUtterance(seg.text);
        if (voice) utt.voice = voice;
        utt.rate = 1.0;
        utt.pitch = 1.0;
        currentUtteranceRef.current = utt;
        utt.onerror = (e) => {
          // 'interrupted' is expected when video moves past segment end — not a real error
          if (e.error === 'interrupted') {
            console.debug(`[PreviewPlayer] TTS seg #${segIndex + 1} interrompido (normal — segmento acabou)`);
            currentUtteranceRef.current = null;
            return;
          }
          pushError('tts', `Erro TTS seg #${segIndex + 1}: ${e.error || 'desconhecido'}`, `text="${seg.text.substring(0, 40)}..."`);
          currentUtteranceRef.current = null;
        };
        utt.onend = () => {
          currentUtteranceRef.current = null;
          checkAndUnfreeze();
        };
        window.speechSynthesis.speak(utt);
        if (healthApi) healthApi.trackAudio('preview', 'previewTtsUtterance', 'utterance', utt);
      } catch (err: any) {
        pushError('tts', `Exceção ao iniciar TTS seg #${segIndex + 1}`, err?.message || String(err));
      }

    } else if (mode === 'premium') {
      // Premium audio from generated segments
      if (!segAudio) {
        pushError('audio', 'segAudio não disponível — hook useSegmentAudio não inicializado.');
        return;
      }

      stopCurrentAudio();
      lastActiveSegRef.current = segIndex;
      try {
        // Use voice-aware lookup: play the version for the currently selected premium voice
        const premiumVoice = currentPremiumVoiceRef.current;
        const version = premiumVoice
          ? segAudio.getActiveVersionForVoice(segIndex, 'premium', premiumVoice)
          : segAudio.getActiveVersion(segIndex, 'premium');
        if (!version) {
          // Silencioso — segmento sem áudio premium gerado para esta voz (esperado)
          return;
        }
        if (!version.audioUrl) {
          pushError('audio', `Seg #${segIndex + 1}: versão premium encontrada mas audioUrl está vazio.`, `versionId=${version.id}`);
          return;
        }

        const audio = new Audio(version.audioUrl);
        currentAudioRef.current = audio;

        audio.onerror = () => {
          const mediaErr = audio.error;
          const errMsg = mediaErr
            ? `code=${mediaErr.code}, msg=${mediaErr.message || 'N/A'}`
            : 'erro desconhecido';
          pushError('audio', `Falha ao carregar áudio premium seg #${segIndex + 1}`, `url=${version.audioUrl?.substring(0, 60)}... ${errMsg}`);
          currentAudioRef.current = null;
        };

        audio.onended = () => {
          currentAudioRef.current = null;
          checkAndUnfreeze();
        };

        audio.play().catch((playErr: any) => {
          pushError('audio', `Falha ao dar play no áudio premium seg #${segIndex + 1}`, playErr?.message || String(playErr));
          currentAudioRef.current = null;
        });

        if (healthApi) healthApi.trackAudio('preview', 'previewSegAudio', 'element', audio);
      } catch (err: any) {
        pushError('audio', `Exceção ao processar áudio premium seg #${segIndex + 1}`, err?.message || String(err));
      }
    }
  }, [segAudio, ttsGetVoice, stopCurrentAudio, healthApi, pushError]);

  // ── Sync loop: watches video.currentTime and triggers segment audio ──
  // PERF: setCurrentTime is throttled to ~4fps (250ms) to avoid re-rendering
  // the entire StudioTab at 60fps. Segment detection still runs every frame.
  const syncLoop = useCallback(() => {
    const vid = videoRef.current;
    if (!vid) {
      rafRef.current = null;
      return;
    }
    if (!isPlayingRef.current) {
      rafRef.current = null;
      return;
    }

    try {
      const now = performance.now();
      let t = vid.currentTime;

      const mode = audioSourceRef.current;
      const lastSegEnd = segmentsRef.current.length > 0 ? segmentsRef.current[segmentsRef.current.length - 1].endSec : 0;
      const vidDuration = isFinite(vid.duration) && !isNaN(vid.duration) ? vid.duration : 0;
      const maxDur = mode !== 'bruto' ? Math.max(vidDuration, lastSegEnd) : vidDuration;

      if (mode !== 'bruto' && isVirtualExtensionRef.current) {
        // Increment virtual time
        const delta = (now - lastSystemTimeRef.current) / 1000;
        playheadTimeRef.current = Math.min(maxDur, playheadTimeRef.current + delta);
        t = playheadTimeRef.current;
      } else {
        // Normal playback
        playheadTimeRef.current = t;
        // Check if we should switch to virtual extension
        if (mode !== 'bruto' && lastSegEnd > vidDuration && t >= vidDuration - 0.05) {
          vid.pause();
          vid.currentTime = Math.max(0, vidDuration - 0.05);
          isVirtualExtensionRef.current = true;
          t = playheadTimeRef.current;
          console.log(`[PreviewPlayer] 🎥 Transicionado para Extensão Virtual em ${vidDuration.toFixed(1)}s (Fim do Segmento: ${lastSegEnd.toFixed(1)}s)`);
        }
      }
      lastSystemTimeRef.current = now;

      // Throttle UI updates to 4fps (250ms) — prevents 60fps re-renders
      if (now - lastTimeUpdateMsRef.current > 250) {
        lastTimeUpdateMsRef.current = now;
        setCurrentTime(t);
      }

      // Check if we reached the end of the virtual timeline
      if (t >= maxDur - 0.02) {
        console.log(`[PreviewPlayer] 🏁 Fim da Timeline Virtual alcançado em ${t.toFixed(2)}s / ${maxDur.toFixed(2)}s`);
        isPlayingRef.current = false;
        setIsPlaying(false);
        stopCurrentAudio();
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          if (healthApi) healthApi.untrackRAF('previewSyncLoop');
          rafRef.current = null;
        }
        vid.currentTime = 0;
        playheadTimeRef.current = 0;
        isVirtualExtensionRef.current = false;
        setCurrentTime(0);
        lastActiveSegRef.current = null;
        return;
      }

      if (mode !== 'bruto') {
        // Find which segment we're in
        const segs = segmentsRef.current;

        // Check if we need to freeze the video/virtual timeline (audio is still playing at segment end)
        if (lastActiveSegRef.current !== null && !isFrozenRef.current) {
          const activeSeg = segs.find(s => s.index === lastActiveSegRef.current);
          if (activeSeg) {
            const audioPlaying = (currentAudioRef.current !== null || currentUtteranceRef.current !== null);
            if (t >= activeSeg.endSec - 0.05 && audioPlaying) {
              // Pause native video if not already in virtual extension
              if (!isVirtualExtensionRef.current) {
                vid.pause();
                vid.currentTime = activeSeg.endSec; // clamp playhead to segment end
              }
              // Freeze the virtual timeline playhead at segment end
              playheadTimeRef.current = activeSeg.endSec;
              t = activeSeg.endSec;
              isFrozenRef.current = true;
              setIsFrozen(true);
              console.log(`[PreviewPlayer] ❄️ Freeze ativado ao final do segmento #${activeSeg.index + 1} (${activeSeg.endSec}s)`);
            }
          }
        }

        // If frozen, wait and schedule next frame without starting new segment audio
        if (isFrozenRef.current) {
          rafRef.current = requestAnimationFrame(syncLoop);
          return;
        }

        let foundIdx: number | null = null;
        for (const seg of segs) {
          if (t >= seg.startSec && t < seg.endSec) {
            foundIdx = seg.index;
            break;
          }
        }

        // Only update state when segment actually changes
        if (foundIdx !== activeSegIdxRef.current) {
          activeSegIdxRef.current = foundIdx;
          setActiveSegIdx(foundIdx);
        }

        // If we entered a new segment, play its audio
        if (foundIdx !== null && foundIdx !== lastActiveSegRef.current) {
          playSegmentAudioForSync(foundIdx);
        } else if (foundIdx === null && lastActiveSegRef.current !== null) {
          // Exited all segments (gap between segments)
          stopCurrentAudio();
          lastActiveSegRef.current = null;
        }
      }
    } catch (err: any) {
      pushError('sync', 'Erro no loop de sincronização — loop continua.', err?.message || String(err));
    }

    rafRef.current = requestAnimationFrame(syncLoop);
    // Track RAF in health after scheduling
    if (healthApi && rafRef.current) {
      healthApi.trackRAF('preview', 'previewSyncLoop', rafRef.current);
    }
  }, [playSegmentAudioForSync, stopCurrentAudio, healthApi, pushError]);

  syncLoopRef.current = syncLoop;

  // ── Play / Pause ──
  const play = useCallback(() => {
    const vid = videoRef.current;
    if (!vid) {
      console.log('[PreviewPlayer][play] Elemento <video> ainda não montado no DOM.');
      return;
    }

    if (!vid.src && !vid.querySelector?.('source')) {
      pushError('play', 'Vídeo sem source — nenhuma URL carregada.', `src="${vid.src}"`);
      return;
    }

    // Check health — don't start if in emergency
    if (healthApi && healthApi.isPhase('EMERGENCY')) {
      pushError('play', 'Play bloqueado — modo de emergência ativo no RecorderHealth.');
      return;
    }

    const mode = audioSourceRef.current;
    // Video is always muted — audio comes from separate elements
    vid.muted = true;

    isPlayingRef.current = true;
    setIsPlaying(true);
    setVideoError(null); // Limpar erro anterior se play funcionar
    lastSystemTimeRef.current = performance.now();

    if (isVirtualExtensionRef.current) {
      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(syncLoop);
        if (healthApi && rafRef.current) {
          healthApi.trackRAF('preview', 'previewSyncLoop', rafRef.current);
        }
      }
      return;
    }

    vid.play().then(() => {
      // ── Bruto mode: play original audio.webm synced with video ──
      if (mode === 'bruto' && originalAudioRef.current) {
        const origAudio = originalAudioRef.current;
        origAudio.currentTime = vid.currentTime;
        origAudio.play().catch((e: any) => {
          pushError('audio', 'Falha ao tocar áudio original (bruto)', e?.message);
        });
      }

      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(syncLoop);
        if (healthApi && rafRef.current) {
          healthApi.trackRAF('preview', 'previewSyncLoop', rafRef.current);
        }
      }
    }).catch((err: any) => {
      const msg = err?.name === 'NotAllowedError'
        ? 'Autoplay bloqueado pelo navegador. Clique no vídeo para iniciar.'
        : `Falha ao iniciar reprodução: ${err?.message || 'desconhecido'}`;
      pushError('play', msg, err?.name || '');
      setVideoError(msg);
      isPlayingRef.current = false;
      setIsPlaying(false);
    });
  }, [syncLoop, healthApi, pushError]);

  const pause = useCallback(() => {
    const vid = videoRef.current;
    if (!vid) return;
    isPlayingRef.current = false;
    setIsPlaying(false);
    try {
      vid.pause();
    } catch (e: any) {
      pushError('play', 'Erro ao pausar vídeo.', e?.message);
    }
    // Pause original audio (bruto mode)
    if (originalAudioRef.current) {
      try { originalAudioRef.current.pause(); } catch (_) {}
    }
    stopCurrentAudio();
    lastActiveSegRef.current = null;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      if (healthApi) healthApi.untrackRAF('previewSyncLoop');
      rafRef.current = null;
    }
  }, [stopCurrentAudio, healthApi, pushError]);

  const togglePlay = useCallback(() => {
    if (isPlaying) pause();
    else play();
  }, [isPlaying, play, pause]);

  // ── Seek ──
  const seek = useCallback((timeSec: number) => {
    const vid = videoRef.current;
    if (!vid) return;
    try {
      if (isNaN(timeSec) || timeSec < 0) {
        pushError('play', `Seek inválido: ${timeSec}`, 'Valor deve ser >= 0');
        return;
      }
      const mode = audioSourceRef.current;
      const lastSegEnd = segmentsRef.current.length > 0 ? segmentsRef.current[segmentsRef.current.length - 1].endSec : 0;
      const vidDuration = isFinite(vid.duration) && !isNaN(vid.duration) ? vid.duration : 0;
      const maxDur = mode !== 'bruto' ? Math.max(vidDuration, lastSegEnd) : vidDuration;

      const clampedTime = Math.min(maxDur, timeSec);

      playheadTimeRef.current = clampedTime;
      lastSystemTimeRef.current = performance.now();

      if (clampedTime < vidDuration) {
        isVirtualExtensionRef.current = false;
        vid.currentTime = clampedTime;
      } else {
        isVirtualExtensionRef.current = true;
        vid.pause();
        vid.currentTime = Math.max(0, vidDuration - 0.05);
      }

      setCurrentTime(clampedTime);
      stopCurrentAudio();
      lastActiveSegRef.current = null;

      // Sync original audio position (bruto mode)
      if (originalAudioRef.current) {
        originalAudioRef.current.currentTime = clampedTime;
      }
    } catch (err: any) {
      pushError('play', `Erro ao fazer seek para ${timeSec}s`, err?.message);
    }
  }, [stopCurrentAudio, pushError]);

  // ── Video event handlers ──
  const onLoadedMetadata = useCallback(() => {
    const vid = videoRef.current;
    if (!vid) return;

    const d = vid.duration;

    if (isFinite(d) && !isNaN(d) && d > 0) {
      // Duration is valid — use it directly
      const segs = segmentsRef.current;
      const lastSegEnd = segs.length > 0 ? segs[segs.length - 1].endSec : 0;
      setDuration(Math.max(d, lastSegEnd));
      setVideoError(null);
      return;
    }

    // ── WebM seek trick ──
    // MediaRecorder-generated WebM files report duration=Infinity because
    // the container header doesn't include duration metadata.
    // Fix: seek to a very large value, wait for 'seeked', then read the
    // now-corrected duration from the browser's decoded stream.
    const onSeeked = () => {
      vid.removeEventListener('seeked', onSeeked);
      const realD = vid.duration;
      if (isFinite(realD) && !isNaN(realD) && realD > 0) {
        const segs = segmentsRef.current;
        const lastSegEnd = segs.length > 0 ? segs[segs.length - 1].endSec : 0;
        setDuration(Math.max(realD, lastSegEnd));
        vid.currentTime = 0; // Reset to beginning
      } else {
        // Fallback: use last segment endSec if available
        const segs = segmentsRef.current;
        if (segs.length > 0) {
          const fallback = segs[segs.length - 1].endSec;
          setDuration(fallback);
          pushError('video',
            `Duração do WebM indeterminada — usando fallback dos segmentos: ${fallback.toFixed(1)}s`,
            `original=${d}, afterSeek=${realD}`
          );
        } else {
          setDuration(0);
          pushError('video', 'Duração do vídeo inválida (NaN/Infinity) e sem segmentos para fallback.', `duration=${realD}`);
        }
        vid.currentTime = 0;
      }
      setVideoError(null);
    };

    vid.addEventListener('seeked', onSeeked);
    // Trigger seek trick — browser will clamp to real end
    vid.currentTime = 1e10;
  }, [pushError]);

  const onEnded = useCallback(() => {
    if (isVirtualExtensionRef.current) {
      console.log('[PreviewPlayer] Ignored native ended event because virtual extension is active.');
      return;
    }
    isPlayingRef.current = false;
    setIsPlaying(false);
    stopCurrentAudio();
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      if (healthApi) healthApi.untrackRAF('previewSyncLoop');
      rafRef.current = null;
    }
    // Reset playhead to start so spacebar can replay
    const vid = videoRef.current;
    if (vid) {
      vid.currentTime = 0;
      setCurrentTime(0);
    }
    playheadTimeRef.current = 0;
    isVirtualExtensionRef.current = false;
    lastActiveSegRef.current = null;
  }, [stopCurrentAudio, healthApi]);

  const onTimeUpdate = useCallback(() => {
    const vid = videoRef.current;
    if (vid && !isPlaying) setCurrentTime(vid.currentTime);
  }, [isPlaying]);

  // ── Video error handler — catch load/decode failures gracefully ──
  const onError = useCallback(() => {
    const vid = videoRef.current;
    let msg: string;
    let details: string | undefined;

    if (!vid || !vid.error) {
      msg = 'Erro desconhecido ao carregar vídeo.';
      details = 'vid.error is null/undefined';
    } else {
      const code = vid.error.code;
      const messages: Record<number, string> = {
        1: 'Carregamento do vídeo foi cancelado.',
        2: 'Erro de rede ao carregar o vídeo. Verifique a conexão.',
        3: 'Erro ao decodificar o vídeo. O arquivo pode estar corrompido.',
        4: 'Formato de vídeo não suportado pelo navegador.',
      };
      msg = messages[code] || `Erro de vídeo (código ${code}).`;
      details = `code=${code}, message="${vid.error.message || 'N/A'}", src="${vid.src?.substring(0, 80) || 'empty'}"`;
    }

    pushError('video', msg, details);
    setVideoError(msg);

    // Stop any playback/sync
    setIsPlaying(false);
    stopCurrentAudio();
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      if (healthApi) healthApi.untrackRAF('previewSyncLoop');
      rafRef.current = null;
    }
  }, [stopCurrentAudio, healthApi, pushError]);

  const clearVideoError = useCallback(() => {
    setVideoError(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCurrentAudio();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        if (healthApi) healthApi.untrackRAF('previewSyncLoop');
      }
    };
  }, [stopCurrentAudio, healthApi]);

  return {
    videoRef,
    isPlaying,
    currentTime,
    duration,
    activeSegIdx,
    isSeeking,
    setIsSeeking,
    videoError,
    errorLog,
    isFrozen,
    // Actions
    play,
    pause,
    togglePlay,
    seek,
    setSegments,
    setAudioSourceMode,
    setOriginalAudioUrl,
    setVoiceRefs,
    clearVideoError,
    clearErrorLog,
    // Video event handlers (attach to <video>)
    onLoadedMetadata,
    onEnded,
    onTimeUpdate,
    onError,
  };
}
